import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDiet } from "@/contexts/DietContext";
import { toast } from "sonner";
import { ArrowLeft, Clock, Users, Flame, ChefHat, Plus, Trash2, Pencil, X, Upload, Lightbulb, Loader2, Lock, Sparkles } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { ImageDropzone } from "@/components/ui/image-dropzone";
import { motion } from "framer-motion";

const SUPABASE_URL = "https://siypiyiaqxqyzztwpcao.supabase.co";
const BUCKET = "cookbook-images";

function imageUrl(path: string | null) {
  if (!path) return null;
  return `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/${path}`;
}

interface Collection {
  id: string; name: string; description: string | null;
  cover_image_path: string | null; recipe_count: number;
  is_active: boolean;
}
interface RecipeSummary {
  id: string; title: string; ingredients: string; image_path: string | null;
  prep_time_minutes: number; servings: number; difficulty: string; total_kcal: number;
  is_active: boolean;
}
interface RecipeDetail extends RecipeSummary {
  collection_id: string;
  preparation: string;
  tips: { id: string; tip: string; sort_order: number }[];
}

const difficultyColor: Record<string, string> = {
  "fácil": "bg-success/15 text-success",
  "médio": "bg-warning/15 text-warning",
  "difícil": "bg-destructive/15 text-destructive",
};

export default function Recipes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedPlan, planLoading } = useDiet();
  const [isAdmin, setIsAdmin] = useState(false);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollection, setSelectedCollection] = useState<Collection | null>(null);
  const [recipes, setRecipes] = useState<RecipeSummary[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<RecipeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [recipeSheetOpen, setRecipeSheetOpen] = useState(false);
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  // Admin modals
  const [showCollectionForm, setShowCollectionForm] = useState(false);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  const [editingCollection, setEditingCollection] = useState<Collection | null>(null);
  const [editingRecipe, setEditingRecipe] = useState<RecipeDetail | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: "collection" | "recipe"; id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    apiFetch<{ isAdmin: boolean }>("api-cookbook", { action: "check-admin" })
      .then((d) => setIsAdmin(d.isAdmin))
      .catch(() => {});
  }, []);

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiFetch<Collection[]>("api-cookbook", { action: "list-collections" });
      setCollections(data);
    } catch (e: any) {
      toast.error(e.message || "Erro ao carregar coleções");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const openCollection = async (col: Collection) => {
    setSelectedCollection(col);
    setLoadingRecipes(true);
    try {
      const data = await apiFetch<RecipeSummary[]>("api-cookbook", { action: "list-recipes", collection_id: col.id });
      setRecipes(data);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoadingRecipes(false);
    }
  };

  const openRecipe = async (r: RecipeSummary) => {
    try {
      const data = await apiFetch<RecipeDetail>("api-cookbook", { action: "get-recipe", recipe_id: r.id });
      setSelectedRecipe(data);
      setRecipeSheetOpen(true);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const goBack = () => {
    setSelectedCollection(null);
    setRecipes([]);
  };

  const toggleActive = async (type: "collection" | "recipe", id: string, currentActive: boolean) => {
    setTogglingIds(prev => new Set(prev).add(id));
    try {
      await apiFetch("api-cookbook", { action: "toggle-active" }, {
        method: "PUT",
        body: { type, id, is_active: !currentActive },
      });
      if (type === "collection") {
        setCollections(prev => prev.map(c => c.id === id ? { ...c, is_active: !currentActive } : c));
      } else {
        setRecipes(prev => prev.map(r => r.id === id ? { ...r, is_active: !currentActive } : r));
      }
      toast.success(!currentActive ? "Ativado com sucesso" : "Desativado com sucesso");
    } catch (e: any) {
      toast.error(e.message || "Erro ao alterar status");
    } finally {
      setTogglingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      if (deleteConfirm.type === "collection") {
        await apiFetch("api-cookbook", { action: "delete-collection", collection_id: deleteConfirm.id }, { method: "DELETE" });
        toast.success("Coleção excluída");
        loadCollections();
      } else {
        await apiFetch("api-cookbook", { action: "delete-recipe", recipe_id: deleteConfirm.id }, { method: "DELETE" });
        toast.success("Receita excluída");
        if (selectedCollection) openCollection(selectedCollection);
        setRecipeSheetOpen(false);
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setDeleting(false);
      setDeleteConfirm(null);
    }
  };

  if (planLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
        </div>
      </div>
    );
  }

  if (!selectedPlan || selectedPlan !== "complete") {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
          <Lock className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-bold text-foreground mb-2">Receitas Exclusivas</h2>
        <p className="text-muted-foreground mb-6 max-w-sm">
          O acesso às receitas está disponível apenas no <strong>Plano PRO FIT MAX</strong>. Acesse coleções curadas com ingredientes, modo de preparo e dicas de chef.
        </p>
        <Button onClick={() => navigate("/plans")} className="gradient-primary text-primary-foreground">
          Ver Planos
        </Button>
      </motion.div>
    );
  }

  // ─── COLLECTIONS VIEW ───
  if (!selectedCollection) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Receitas</h1>
            <p className="text-sm text-muted-foreground mt-1">Coleções exclusivas de receitas saudáveis</p>
          </div>
          {isAdmin && (
            <Button size="sm" onClick={() => setShowCollectionForm(true)}>
              <Plus className="w-4 h-4 mr-1" /> Nova Coleção
            </Button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-48 rounded-xl" />)}
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Nenhuma coleção disponível ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {collections.map(col => (
              <div
                key={col.id}
                className={`group relative rounded-xl overflow-hidden border border-border bg-card cursor-pointer hover:shadow-lg transition-shadow ${!col.is_active && isAdmin ? "opacity-60" : ""}`}
                onClick={() => openCollection(col)}
              >
                <div className="h-36 bg-muted overflow-hidden">
                  {col.cover_image_path ? (
                    <img src={imageUrl(col.cover_image_path)!} alt={col.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ChefHat className="w-10 h-10 text-muted-foreground/30" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-foreground">{col.name}</h3>
                    {isAdmin && (
                      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                        <span className="text-[10px] text-muted-foreground">{col.is_active ? "Ativa" : "Inativa"}</span>
                        <Switch
                          checked={col.is_active}
                          disabled={togglingIds.has(col.id)}
                          onCheckedChange={() => toggleActive("collection", col.id, col.is_active)}
                        />
                      </div>
                    )}
                  </div>
                  {col.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{col.description}</p>}
                  <p className="text-xs text-muted-foreground mt-2">{col.recipe_count} receita{col.recipe_count !== 1 ? "s" : ""}</p>
                </div>
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingCollection(col); }}
                      className="p-1.5 rounded-lg bg-card/80 backdrop-blur text-foreground hover:text-primary"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: "collection", id: col.id, name: col.name }); }}
                      className="p-1.5 rounded-lg bg-card/80 backdrop-blur text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {(showCollectionForm || editingCollection) && (
          <CollectionFormDialog
            collection={editingCollection}
            onClose={() => { setShowCollectionForm(false); setEditingCollection(null); }}
            onSaved={() => { setShowCollectionForm(false); setEditingCollection(null); loadCollections(); }}
          />
        )}

        <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
              <AlertDialogDescription>
                {deleteConfirm?.type === "collection"
                  ? `Tem certeza que deseja excluir a coleção "${deleteConfirm?.name}" e todas as suas receitas? Esta ação não pode ser desfeita.`
                  : `Tem certeza que deseja excluir a receita "${deleteConfirm?.name}"? Esta ação não pode ser desfeita.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ─── RECIPES LIST VIEW ───
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={goBack} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-foreground truncate">{selectedCollection.name}</h1>
          {selectedCollection.description && <p className="text-sm text-muted-foreground truncate">{selectedCollection.description}</p>}
        </div>
        {isAdmin && (
          <Button size="sm" onClick={() => setShowRecipeForm(true)}>
            <Plus className="w-4 h-4 mr-1" /> Nova Receita
          </Button>
        )}
      </div>

      {loadingRecipes ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1,2,3].map(i => <Skeleton key={i} className="h-56 rounded-xl" />)}
        </div>
      ) : recipes.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>Nenhuma receita nesta coleção.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {recipes.map(r => (
            <div
              key={r.id}
              className={`group relative rounded-xl overflow-hidden border border-border bg-card cursor-pointer hover:shadow-lg transition-shadow ${!r.is_active && isAdmin ? "opacity-60" : ""}`}
              onClick={() => openRecipe(r)}
            >
              <div className="h-40 bg-muted overflow-hidden">
                {r.image_path ? (
                  <img src={imageUrl(r.image_path)!} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ChefHat className="w-10 h-10 text-muted-foreground/30" />
                  </div>
                )}
              </div>
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-foreground line-clamp-1">{r.title}</h3>
                  {isAdmin && (
                    <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                      <span className="text-[10px] text-muted-foreground">{r.is_active ? "Ativa" : "Inativa"}</span>
                      <Switch
                        checked={r.is_active}
                        disabled={togglingIds.has(r.id)}
                        onCheckedChange={() => toggleActive("recipe", r.id, r.is_active)}
                      />
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary" className="text-[11px] gap-1"><Clock className="w-3 h-3" />{r.prep_time_minutes} min</Badge>
                  <Badge variant="secondary" className="text-[11px] gap-1"><Users className="w-3 h-3" />{r.servings} porç.</Badge>
                  <Badge variant="secondary" className="text-[11px] gap-1"><Flame className="w-3 h-3" />{r.total_kcal} kcal/porção</Badge>
                  <Badge className={`text-[11px] border-0 ${difficultyColor[r.difficulty] || "bg-muted text-muted-foreground"}`}>{r.difficulty}</Badge>
                </div>
              </div>
              {isAdmin && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const data = await apiFetch<RecipeDetail>("api-cookbook", { action: "get-recipe", recipe_id: r.id });
                        setEditingRecipe(data);
                      } catch (err: any) { toast.error(err.message); }
                    }}
                    className="p-1.5 rounded-lg bg-card/80 backdrop-blur text-foreground hover:text-primary"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ type: "recipe", id: r.id, name: r.title }); }}
                    className="p-1.5 rounded-lg bg-card/80 backdrop-blur text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recipe detail sheet */}
      <Sheet open={recipeSheetOpen} onOpenChange={setRecipeSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
          {selectedRecipe && (
            <div>
              {selectedRecipe.image_path && (
                <div className="h-52 overflow-hidden">
                  <img src={imageUrl(selectedRecipe.image_path)!} alt={selectedRecipe.title} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-6 space-y-5">
                <SheetHeader className="text-left p-0">
                  <SheetTitle className="text-xl">{selectedRecipe.title}</SheetTitle>
                </SheetHeader>

                <div className="flex flex-wrap gap-2">
                  <Badge variant="secondary" className="gap-1"><Clock className="w-3.5 h-3.5" />{selectedRecipe.prep_time_minutes} min</Badge>
                  <Badge variant="secondary" className="gap-1"><Users className="w-3.5 h-3.5" />{selectedRecipe.servings} porções</Badge>
                  <Badge variant="secondary" className="gap-1"><Flame className="w-3.5 h-3.5" />{selectedRecipe.total_kcal} kcal/porção</Badge>
                  <Badge className={`border-0 ${difficultyColor[selectedRecipe.difficulty] || "bg-muted text-muted-foreground"}`}>{selectedRecipe.difficulty}</Badge>
                </div>

                {/* Ingredientes */}
                {selectedRecipe.ingredients && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-foreground flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> Ingredientes</h3>
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{selectedRecipe.ingredients}</p>
                  </div>
                )}

                {/* Modo de Preparo */}
                {selectedRecipe.preparation && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-foreground flex items-center gap-2"><ChefHat className="w-4 h-4 text-primary" /> Modo de Preparo</h3>
                    <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{selectedRecipe.preparation}</p>
                  </div>
                )}

                {/* Tips */}
                {selectedRecipe.tips.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-foreground flex items-center gap-2"><Lightbulb className="w-4 h-4 text-warning" /> Dicas de Chef</h3>
                    <ul className="space-y-2">
                      {selectedRecipe.tips.map(t => (
                        <li key={t.id} className="flex gap-2 text-sm text-muted-foreground">
                          <span className="text-warning mt-0.5">•</span>
                          <span>{t.tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {isAdmin && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => { setRecipeSheetOpen(false); setEditingRecipe(selectedRecipe); }}>
                      <Pencil className="w-4 h-4 mr-1" /> Editar
                    </Button>
                    <Button variant="destructive" size="sm" className="flex-1" onClick={() => setDeleteConfirm({ type: "recipe", id: selectedRecipe.id, name: selectedRecipe.title })}>
                      <Trash2 className="w-4 h-4 mr-1" /> Excluir
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {(showRecipeForm || editingRecipe) && (
        <RecipeFormDialog
          collectionId={selectedCollection.id}
          recipe={editingRecipe}
          onClose={() => { setShowRecipeForm(false); setEditingRecipe(null); }}
          onSaved={() => { setShowRecipeForm(false); setEditingRecipe(null); openCollection(selectedCollection); }}
        />
      )}

      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteConfirm?.type === "collection"
                ? `Tem certeza que deseja excluir a coleção "${deleteConfirm?.name}" e todas as suas receitas? Esta ação não pode ser desfeita.`
                : `Tem certeza que deseja excluir a receita "${deleteConfirm?.name}"? Esta ação não pode ser desfeita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── COLLECTION FORM ───
function CollectionFormDialog({ collection, onClose, onSaved }: { collection?: Collection | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!collection;
  const [name, setName] = useState(collection?.name || "");
  const [description, setDescription] = useState(collection?.description || "");
  const [cover, setCover] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return toast.error("Informe o nome da coleção");
    setSaving(true);
    try {
      let compressedCover = cover;
      if (cover) {
        const { compressImage } = await import("@/lib/imageCompressor");
        compressedCover = await compressImage(cover);
      }
      const fd = new FormData();
      if (isEdit) {
        fd.append("collection_id", collection!.id);
        fd.append("name", name.trim());
        fd.append("description", description.trim());
        if (compressedCover) fd.append("cover", compressedCover);
        await apiFetch("api-cookbook", { action: "update-collection" }, { method: "PUT", formData: fd });
        toast.success("Coleção atualizada!");
      } else {
        fd.append("name", name.trim());
        if (description.trim()) fd.append("description", description.trim());
        if (compressedCover) fd.append("cover", compressedCover);
        await apiFetch("api-cookbook", { action: "create-collection" }, { method: "POST", formData: fd });
        toast.success("Coleção criada!");
      }
      onSaved();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? "Editar Coleção" : "Nova Coleção"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Nome *</Label><Input value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Sobremesas Fit" /></div>
          <div><Label>Descrição</Label><Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Descrição da coleção" rows={2} /></div>
          <div>
            <Label className="mb-2 block">Imagem de capa</Label>
            <ImageDropzone
              onFileSelect={(file) => setCover(file)}
              preview={cover ? URL.createObjectURL(cover) : undefined}
              label="Arraste ou clique para enviar"
              hint="PNG, JPG ou WEBP"
              compact
            />
          </div>
          <Button className="w-full" onClick={submit} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : isEdit ? <Pencil className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {isEdit ? "Salvar Alterações" : "Criar Coleção"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── RECIPE FORM ───
function RecipeFormDialog({ collectionId, recipe, onClose, onSaved }: { collectionId: string; recipe?: RecipeDetail | null; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!recipe;
  const [title, setTitle] = useState(recipe?.title || "");
  const [ingredients, setIngredients] = useState(recipe?.ingredients || "");
  const [preparation, setPreparation] = useState(recipe?.preparation || "");
  const [prepTime, setPrepTime] = useState(recipe ? String(recipe.prep_time_minutes) : "");
  const [servings, setServings] = useState(recipe ? String(recipe.servings) : "");
  const [difficulty, setDifficulty] = useState(recipe?.difficulty || "fácil");
  const [totalKcal, setTotalKcal] = useState(recipe ? String(recipe.total_kcal) : "");
  const [image, setImage] = useState<File | null>(null);
  const [tips, setTips] = useState<string[]>(recipe?.tips?.length ? recipe.tips.map(t => t.tip) : [""]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Informe o título da receita");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("ingredients", ingredients.trim());
      fd.append("preparation", preparation.trim());
      fd.append("prep_time_minutes", prepTime || "0");
      fd.append("servings", servings || "1");
      fd.append("difficulty", difficulty);
      fd.append("total_kcal", totalKcal || "0");
      fd.append("tips", JSON.stringify(tips.filter(t => t.trim())));
      if (image) {
        const { compressImage } = await import("@/lib/imageCompressor");
        const compressedImage = await compressImage(image);
        fd.append("image", compressedImage);
      }

      if (isEdit) {
        fd.append("recipe_id", recipe!.id);
        await apiFetch("api-cookbook", { action: "update-recipe" }, { method: "PUT", formData: fd });
        toast.success("Receita atualizada!");
      } else {
        fd.append("collection_id", collectionId);
        await apiFetch("api-cookbook", { action: "create-recipe" }, { method: "POST", formData: fd });
        toast.success("Receita criada!");
      }
      onSaved();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? "Editar Receita" : "Nova Receita"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Título *</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Mousse de Maracujá Fit" /></div>
          <div><Label>Ingredientes</Label><Textarea value={ingredients} onChange={e => setIngredients(e.target.value)} placeholder="Liste os ingredientes da receita" rows={4} /></div>
          <div>
            <Label className="mb-2 block">Imagem da receita</Label>
            <ImageDropzone
              onFileSelect={(file) => setImage(file)}
              preview={image ? URL.createObjectURL(image) : undefined}
              label="Arraste ou clique para enviar"
              hint="PNG, JPG ou WEBP"
              compact
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Tempo (min)</Label><Input type="number" value={prepTime} onChange={e => setPrepTime(e.target.value)} placeholder="30" /></div>
            <div><Label>Porções</Label><Input type="number" value={servings} onChange={e => setServings(e.target.value)} placeholder="4" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Dificuldade</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fácil">Fácil</SelectItem>
                  <SelectItem value="médio">Médio</SelectItem>
                  <SelectItem value="difícil">Difícil</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Média kcal p/ porção</Label><Input type="number" value={totalKcal} onChange={e => setTotalKcal(e.target.value)} placeholder="250" /></div>
          </div>

          {/* Modo de Preparo */}
          <div>
            <Label>Modo de Preparo</Label>
            <Textarea value={preparation} onChange={e => setPreparation(e.target.value)} placeholder="Descreva o modo de preparo da receita" rows={5} />
          </div>

          {/* Tips */}
          <div className="space-y-2">
            <Label>Dicas de Chef</Label>
            {tips.map((t, i) => (
              <div key={i} className="flex gap-2 items-start">
                <span className="text-warning mt-2.5">•</span>
                <Input value={t} onChange={e => { const n = [...tips]; n[i] = e.target.value; setTips(n); }} placeholder="Dica" className="flex-1" />
                {tips.length > 1 && (
                  <button onClick={() => setTips(tips.filter((_, j) => j !== i))} className="mt-2 text-destructive"><X className="w-4 h-4" /></button>
                )}
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setTips([...tips, ""])}><Plus className="w-3 h-3 mr-1" /> Dica</Button>
          </div>

          <Button className="w-full" onClick={submit} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : isEdit ? <Pencil className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {isEdit ? "Salvar Alterações" : "Criar Receita"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
